import ApiService from "@/core/services/api.service";

const INSTRUCTOR_ENDPOINT = "/api/instructor";
const MY_INSTRUCTORS_ENDPOINT = "/api/my-instructors";
const USER_ENDPOINT = "/api/user";
const FALLBACK_PHOTO = "https://cdn.anzera.com.tr/softinya/not-found.png";

export const INSTRUCTOR_TYPES = Object.freeze({
  INTERNAL: "internal",
  EXTERNAL: "external",
});

export const INSTRUCTOR_STATUSES = Object.freeze({
  ACTIVE: 1,
  PASSIVE: 0,
});

const unwrapEntity = (response) => {
  const payload = response?.data;
  if (
    payload &&
    payload.data &&
    !Array.isArray(payload.data) &&
    typeof payload.data === "object"
  ) {
    return payload.data;
  }
  return payload || {};
};

const unwrapCollection = (response) => {
  const payload = response?.data || {};

  if (Array.isArray(payload)) {
    return {
      data: payload,
      current_page: 1,
      last_page: 1,
      per_page: payload.length,
      total: payload.length,
    };
  }

  if (Array.isArray(payload.data)) {
    return {
      ...payload,
      ...(payload.meta || {}),
      data: payload.data,
    };
  }
  if (payload.data && Array.isArray(payload.data.data)) {
    return {
      ...payload.data,
      ...(payload.data.meta || payload.meta || {}),
      data: payload.data.data,
    };
  }

  return {
    data: [],
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  };
};

const getRelationName = (relation) => {
  if (!relation) return "";
  if (typeof relation === "string") return relation;
  return relation.name || relation.title || "";
};

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const uniqueById = (items = []) =>
  Array.from(
    new Map(
      items
        .filter((item) => item && item.id)
        .map((item) => [String(item.id), item])
    ).values()
  );

export const normalizeUser = (user = {}) => {
  const fullName =
    [user.name, user.surname].filter(Boolean).join(" ").trim() ||
    user.full_name ||
    "";

  return {
    id: Number(user.id),
    name: fullName,
    email: user.email || user.mail || "",
    photo: user.photo_link || user.avatar || user.photo || FALLBACK_PHOTO,
    title: getRelationName(user.title) || user.title_name || "",
    position: getRelationName(user.position) || user.position_name || "",
    department:
      getRelationName(user.department) || user.department_name || "",
    statusId: Number(user.status_id),
    raw: user,
  };
};

export const normalizeInstructor = (item = {}) => {
  const userSource = item.user || item;
  const user = normalizeUser(userSource);
  const trainings = uniqueById(ensureArray(item.trainings));

  return {
    id: Number(item.instructor_id || item.id),
    userId: Number(item.user_id || user.id),
    user,
    name: user.name || item.full_name || item.name || "",
    photo: user.photo || FALLBACK_PHOTO,
    title:
      user.title
      || user.position
      || item.title
      || item.title_name
      || item.position_name
      || "",
    department:
      user.department
      || item.department
      || item.department_name
      || "",
    type: item.type || INSTRUCTOR_TYPES.INTERNAL,
    institutionName: item.institution_name || "",
    expertiseAreas: ensureArray(item.expertise_areas),
    bio: item.bio || "",
    experienceYears: Number(item.experience_years) || null,
    educationsCertificates: ensureArray(item.educations_certificates),
    linkedinUrl: item.linkedin_url || "",
    statusId: Number(item.status_id),
    createdAt: item.created_at || "",
    assignmentsCount: Number(item.assignments_count) || 0,
    contentsCount: Number(item.contents_count) || 0,
    trainings,
    trainingCount: trainings.length,
    raw: item,
  };
};

const instructorService = {
  async listMine(params = {}) {
    const response = await ApiService.query(MY_INSTRUCTORS_ENDPOINT, params);
    return unwrapCollection(response)
      .data.map(normalizeInstructor)
      .filter((instructor) => Number.isFinite(instructor.id));
  },

  async getMine(id) {
    const response = await ApiService.get(MY_INSTRUCTORS_ENDPOINT, id);
    return normalizeInstructor(unwrapEntity(response));
  },

  async list(params) {
    const response = await ApiService.query(INSTRUCTOR_ENDPOINT, params);
    const pagination = unwrapCollection(response);
    const items = pagination.data.map(normalizeInstructor);

    return {
      items,
      pagination: {
        ...pagination,
        data: items,
      },
    };
  },

  async get(id) {
    const response = await ApiService.get(INSTRUCTOR_ENDPOINT, id);
    return normalizeInstructor(unwrapEntity(response));
  },

  async create(payload) {
    const response = await ApiService.post(INSTRUCTOR_ENDPOINT, payload);
    return normalizeInstructor(unwrapEntity(response));
  },

  async update(id, payload) {
    const response = await ApiService.update(INSTRUCTOR_ENDPOINT, id, payload);
    return normalizeInstructor(unwrapEntity(response));
  },

  async updateStatus(id, statusId) {
    return this.update(id, { status_id: statusId });
  },

  async bulkUpdateStatus(ids, statusId) {
    return ApiService.put(`${INSTRUCTOR_ENDPOINT}/bulk-status`, {
      instructors: ids,
      status_id: Number(statusId),
    });
  },

  async remove(id) {
    return ApiService.delete(`${INSTRUCTOR_ENDPOINT}/${id}`);
  },

  async bulkRemove(ids) {
    return ApiService.delete(`${INSTRUCTOR_ENDPOINT}/bulk-destroy`, {
      data: {
        instructors: ids,
      },
    });
  },

  async counts() {
    const countParams = {
      page: 1,
      per_page: 1,
      sort: "id",
      order: "desc",
    };
    const [allResponse, activeResponse, passiveResponse] = await Promise.all([
      ApiService.query(INSTRUCTOR_ENDPOINT, countParams),
      ApiService.query(INSTRUCTOR_ENDPOINT, {
        ...countParams,
        status_id: INSTRUCTOR_STATUSES.ACTIVE,
      }),
      ApiService.query(INSTRUCTOR_ENDPOINT, {
        ...countParams,
        status_id: INSTRUCTOR_STATUSES.PASSIVE,
      }),
    ]);

    const total = unwrapCollection(allResponse);
    const active = unwrapCollection(activeResponse);
    const passive = unwrapCollection(passiveResponse);

    return {
      active: Number(active.total) || 0,
      passive: Number(passive.total) || 0,
      total: Number(total.total) || 0,
    };
  },

  export(params = {}) {
    return ApiService.post(`${INSTRUCTOR_ENDPOINT}/export`, params, {
      responseType: "blob",
    });
  },

  async listUsers(params = {}) {
    const response = await ApiService.query(USER_ENDPOINT, {
      page: 1,
      per_page: 1000,
      order: "desc",
      sort: "created_at",
      status_id: 1,
      ...params,
    });
    return unwrapCollection(response)
      .data.map(normalizeUser)
      .filter((user) => !user.raw.instructor);
  },

  async listActiveInstructorOptions(params = {}) {
    const response = await ApiService.query(USER_ENDPOINT, {
      is_instructor: 1,
      like: "",
      page: 1,
      per_page: 1000,
      order: "asc",
      sort: "name",
      status_id: 1,
      ...params,
    });

    return unwrapCollection(response)
      .data.map((userData) => {
        const user = normalizeUser(userData);
        const instructor = normalizeInstructor({
          ...(userData.instructor || {}),
          user: userData,
        });

        return {
          ...instructor,
          user,
          name: user.name,
        };
      })
      .filter((instructor) => Number.isFinite(instructor.id));
  },
};

export default instructorService;
